import { useEffect, useRef } from 'react';

/**
 * One-shot peek hint: on first visit per session, after venues load,
 * briefly expand the map ~38% of the viewport then snap back. Gives
 * users an unmistakable "this slides up" affordance hint.
 *
 * Params:
 *   loading           — true while venues are still loading; peek waits.
 *   miniHeight        — collapsed height to snap back to.
 *   animateMapResize  — from `useGuestMapDrag`, drives the height + rAF resize.
 *   storageKey        — sessionStorage key used to remember it's been shown.
 */
export function useGuestMapPeek({
  loading,
  miniHeight,
  animateMapResize,
  hasInteractedRef,
  storageKey = 'allure-map-peeked',
}) {
  const hasPeekedRef = useRef(
    typeof window !== 'undefined' && !!sessionStorage.getItem(storageKey),
  );
  useEffect(() => {
    if (loading) return undefined;
    if (hasPeekedRef.current) return undefined;
    hasPeekedRef.current = true;
    sessionStorage.setItem(storageKey, '1');

    const peekH = Math.min(
      miniHeight + 180,
      Math.floor((typeof window !== 'undefined' ? window.innerHeight : 780) * 0.38),
    );
    const peek = (height) => {
      if (!hasInteractedRef.current) animateMapResize(height);
    };
    const t1 = setTimeout(() => peek(peekH), 700);
    const t2 = setTimeout(() => peek(miniHeight), 1760);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // animateMapResize / miniHeight / storageKey intentionally omitted —
    // peek runs once per session and shouldn't retrigger on re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
}
